import React from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import CandidateInfo from './CandidateInfo'
import CoachLoadingStates from './CoachLoadingStates'
import CoachTextChatWrap from './CoachTextChatWrap'
import CoachVideoConversation from './CoachVideoConversation'
import { useCoachChat } from './CoachChatContext'

/**
 * VideoChat component - displays a video chat with candidate info and text chat
 * Always uses chat-1
 * @param {Object} props
 * @param {Object} props.candidate - The candidate being chatted with
 * @param {Array} props.messages - Array of chat messages (filtered by chatId)
 * @param {Object} props.selectedCandidate - The selected candidate for this chat slot
 */
const VideoChat = ({ candidate, messages, selectedCandidate }) => {
  const { connectionState, chatManager, getDarkModeClasses } = useCoachChat()
  const { t } = useTranslation('coach_chat')

  // Get candidate token for CandidateInfo
  // First try to get from candidate object (cached when candidate leaves), then from chat object, then from room API field, then fallback to candidate.username
  // (candidate info is cached using candidate.username as the key)
  const easyrtc = chatManager?.rtcService?.getEasyRTC()

  // Try to get candidateToken from candidate object first (set by getCandidateFromChat)
  const candidateTokenFromCandidate = candidate?.candidateToken

  // Try to get candidateToken from the active chat object (persists when candidate leaves)
  const activeChat = connectionState.activeChats?.find(
    (chat) =>
      chat.candidateEasyrtcid === candidate?.easyrtcid ||
      chat.candidateId === candidate?.easyrtcid,
  )
  const candidateTokenFromChat = activeChat?.candidateToken

  // Fallback to room API field if not in chat object
  const candidateTokenFromRoom =
    easyrtc &&
    candidate?.easyrtcid &&
    easyrtc.getRoomApiField(
      connectionState.chatRoom,
      candidate.easyrtcid,
      'chatToken',
    )

  const candidateToken =
    candidateTokenFromCandidate ||
    candidateTokenFromChat ||
    candidateTokenFromRoom ||
    candidate?.username

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-2">
      <div
        className="grid min-h-0 w-full flex-1 gap-4"
        style={{
          gridTemplateColumns: '1fr 400px',
          gridTemplateRows: '1fr',
        }}
      >
        {/* Left: Video Conversation */}
        <div className="flex min-h-0 flex-col overflow-hidden">
          <CoachLoadingStates
            chatMode="video"
            hasLocalVideo={connectionState.hasLocalVideo}
            hasRemoteVideo={connectionState.hasRemoteVideo}
            cameraReady={connectionState.cameraReady}
          />
          {connectionState.cameraReady && (
            <div className="min-h-0 flex-1">
              <CoachVideoConversation
                candidate={candidate}
                bgEffectsEnabled={false}
              />
            </div>
          )}
        </div>

        {/* Right: Candidate Info and Text Chat */}
        <div
          className={`flex min-h-0 flex-col gap-4 overflow-hidden rounded-lg border ${getDarkModeClasses(
            'border-gray-200 bg-gray-50',
            'border-gray-600 bg-gray-800',
          )}`}
        >
          {/* Candidate Info on top — CTS-9777: pause/mute live here */}
          <div className="flex flex-shrink-0 flex-col">
            {candidateToken ? (
              <CandidateInfo
                chatToken={candidateToken}
                slotCandidateEasyrtcid={candidate?.easyrtcid}
                chatMode="video"
              />
            ) : (
              <div
                className={`flex h-full items-center justify-center text-sm ${getDarkModeClasses(
                  'text-gray-500',
                  'text-gray-400',
                )}`}
              >
                Candidate Info
                <br />
                <span className="text-xs">(Not available)</span>
              </div>
            )}
          </div>

          {/* Text Chat Wrap below - hidden when candidate has text disabled (e.g. mobile video) */}
          <div className="min-h-0 flex-1 overflow-hidden">
            {activeChat?.textDisabled ? (
              <div
                className={`flex flex-1 items-center justify-center text-sm ${getDarkModeClasses(
                  'text-gray-500',
                  'text-gray-400',
                )}`}
              >
                {t('video_chat.text_chat_unavailable', {
                  defaultValue:
                    "Text chat is not available on the candidate's device.",
                })}
              </div>
            ) : (
              <CoachTextChatWrap
                messages={messages}
                candidate={candidate}
                selectedCandidate={selectedCandidate}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoChat
